import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { jsonDiff } from '@vtf/shared';
import { api, errorText, getToken, refresh } from '../lib/api';
import { useMe } from '../lib/session';
import { fmtTime } from '../lib/format';
import { Button, Card, CardHeader, cx, Empty, ErrorText, Field, Input, PageHeader, Pill, Select } from '../components/ui';

/** INT-ADM-02: searchable, exportable, read-only audit log. */

interface Entry {
  id: string;
  createdAt: string;
  actor: { id: string; name: string; email: string } | null;
  action: string;
  entityType: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  userAgent: string | null;
}
interface Page {
  items: Entry[];
  next: string | null;
  total: number | null;
}
interface Facets {
  actions: string[];
  entityTypes: string[];
  actors: { id: string; name: string; email: string }[];
}

const EMPTY = { from: '', to: '', actorId: '', action: '', entityType: '', entityId: '', q: '' };
type Filters = typeof EMPTY;

const toQuery = (f: Filters) => new URLSearchParams(Object.entries(f).filter(([, v]) => v.trim() !== '')).toString();

const actionTone = (a: string): 'down' | 'warn' | 'accent' | 'neutral' | 'up' => (/failed|reuse|locked/.test(a) ? 'down' : /^setting\.|^riskPolicy\.|^fx\.|^user\.roles/.test(a) ? 'warn' : /^auth\./.test(a) ? 'accent' : 'neutral');

const show = (v: unknown) => (v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v));

function Diff({ e }: { e: Entry }) {
  const [raw, setRaw] = useState(false);
  // a created or removed record diffs against an empty object, so each field gets its own row
  const obj = (v: unknown) => typeof v === 'object' && v !== null && !Array.isArray(v);
  const changes = e.before == null && e.after == null ? [] : jsonDiff(e.before ?? (obj(e.after) ? {} : undefined), e.after ?? (obj(e.before) ? {} : undefined));
  return (
    <div className="flex flex-col gap-2.5 bg-raised px-4 py-3">
      {changes.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] border-collapse text-sm">
            <caption className="pb-1.5 text-left text-xs text-muted">{e.before == null ? 'Recorded values' : e.after == null ? 'Values before removal' : 'What changed'}</caption>
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="py-1.5 pr-3 font-medium">Field</th>
                <th className="py-1.5 pr-3 font-medium">Before</th>
                <th className="py-1.5 font-medium">After</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((c) => (
                <tr key={c.path} className="border-t border-line align-top">
                  <td className="py-1.5 pr-3 font-mono text-xs text-ink2">{c.path}</td>
                  <td className={cx('py-1.5 pr-3 font-mono text-xs break-all', c.kind === 'added' ? 'text-muted' : 'text-down')}>{c.kind === 'added' ? '—' : show(c.before)}</td>
                  <td className={cx('py-1.5 font-mono text-xs break-all', c.kind === 'removed' ? 'text-muted' : 'text-up')}>{c.kind === 'removed' ? '—' : show(c.after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="m-0 text-sm text-muted">{e.before == null && e.after == null ? 'No before/after data recorded for this action.' : 'Saved with the same values as before — nothing changed.'}</p>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
        <span>Entry {e.id}</span>
        {e.ip && <span>IP {e.ip}</span>}
        {e.userAgent && <span className="max-w-full truncate">{e.userAgent}</span>}
        {(e.before != null || e.after != null) && (
          <button type="button" className="cursor-pointer text-accent" onClick={() => setRaw(!raw)} aria-expanded={raw}>
            {raw ? 'Hide' : 'Show'} full JSON
          </button>
        )}
      </div>
      {raw && (
        <div className="grid gap-2 md:grid-cols-2">
          {(['before', 'after'] as const).map((k) => (
            <div key={k} className="min-w-0">
              <span className="text-xs text-muted">{k}</span>
              <pre className="m-0 max-h-[280px] overflow-auto rounded-md border border-line bg-field p-2 font-mono text-xs text-ink2">{JSON.stringify(e[k], null, 2) ?? 'null'}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Downloads the export with the in-memory bearer token (a plain link can't carry it). */
async function downloadExport(query: string): Promise<{ rows: number; truncatedFrom: number | null }> {
  const go = () => fetch(`/api/v1/audit/export${query ? `?${query}` : ''}`, { credentials: 'include', headers: { Authorization: `Bearer ${getToken() ?? ''}` } });
  let res = await go();
  if (res.status === 401 && (await refresh())) res = await go();
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message ?? `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'vtf-audit.csv';
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  const t = res.headers.get('x-vtf-export-truncated');
  return { rows: Number(res.headers.get('x-vtf-export-rows') ?? 0), truncatedFrom: t ? Number(t) : null };
}

export function Audit() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [openId, setOpenId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<{ tone: 'ok' | 'warn' | 'err'; text: string } | null>(null);
  const query = toQuery(filters);
  const facets = useQuery({ queryKey: ['audit-facets'], queryFn: () => api<Facets>('/audit/facets'), staleTime: 60_000 });
  const pages = useInfiniteQuery({
    queryKey: ['audit', query],
    queryFn: ({ pageParam }) => api<Page>(`/audit?limit=50${query ? `&${query}` : ''}${pageParam ? `&cursor=${pageParam}` : ''}`),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const items = pages.data?.pages.flatMap((p) => p.items) ?? [];
  const total = pages.data?.pages[0]?.total ?? null;
  const prefixes = [...new Set((facets.data?.actions ?? []).map((a) => a.split('.')[0]!))].filter((p) => (facets.data?.actions ?? []).filter((a) => a.startsWith(`${p}.`)).length > 1);
  const set = (k: keyof Filters) => (e: { target: { value: string } }) => setDraft({ ...draft, [k]: e.target.value });

  const exportCsv = async () => {
    setExporting(true);
    setExportMsg(null);
    try {
      const r = await downloadExport(query);
      setExportMsg(r.truncatedFrom ? { tone: 'warn', text: `Exported the newest ${r.rows.toLocaleString('en-GB')} of ${r.truncatedFrom.toLocaleString('en-GB')} matching entries. Narrow the dates to export the rest.` } : { tone: 'ok', text: `Exported ${r.rows.toLocaleString('en-GB')} entr${r.rows === 1 ? 'y' : 'ies'}.` });
    } catch (e) {
      setExportMsg({ tone: 'err', text: errorText(e) });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Audit log"
        sub="Sign-ins, role changes, approvals and configuration changes. Entries can't be edited or deleted."
        right={
          <Button variant="primary" onClick={() => void exportCsv()} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        }
      />
      <Card className="p-4">
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setFilters(draft);
            setOpenId(null);
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="From (date)">
              <Input type="date" value={draft.from} onChange={set('from')} />
            </Field>
            <Field label="To (date)">
              <Input type="date" value={draft.to} onChange={set('to')} />
            </Field>
            <Field label="Person">
              <Select value={draft.actorId} onChange={set('actorId')}>
                <option value="">Anyone</option>
                {facets.data?.actors.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Action">
              <Select value={draft.action} onChange={set('action')}>
                <option value="">Any action</option>
                {prefixes.map((p) => (
                  <option key={`${p}.*`} value={`${p}.*`}>
                    All {p}.* actions
                  </option>
                ))}
                {facets.data?.actions.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Entity type">
              <Select value={draft.entityType} onChange={set('entityType')}>
                <option value="">Any</option>
                {facets.data?.entityTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Entity ID">
              <Input value={draft.entityId} onChange={set('entityId')} placeholder="exact ID" />
            </Field>
            <Field label="Search text" hint="Matches action, names, IP and the recorded values" className="sm:col-span-2">
              <Input type="search" value={draft.q} onChange={set('q')} placeholder="e.g. PROGRESS_REPORT_INTERVAL or wrong_password" />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" variant="primary">
              Search
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setDraft(EMPTY);
                setFilters(EMPTY);
              }}
            >
              Clear filters
            </Button>
            <span className="text-xs text-muted">Dates are whole days in {tz}. Export uses the filters you searched with.</span>
          </div>
        </form>
      </Card>
      {exportMsg && (
        <p role="status" className={cx('m-0 rounded-lg border px-3 py-2 text-sm', exportMsg.tone === 'ok' ? 'border-[#1e5a3c] bg-up-bg text-up' : exportMsg.tone === 'warn' ? 'border-[#5a4316] bg-warn-bg text-warn' : 'border-[#6a2a27] bg-down-bg text-down')}>
          {exportMsg.text}
        </p>
      )}
      <ErrorText error={pages.error ? errorText(pages.error) : null} />

      <Card>
        <CardHeader title={<>Entries {total !== null && <span className="font-normal text-muted">· {total.toLocaleString('en-GB')}</span>}</>} right={<span className="text-xs text-muted">Newest first · select a row for details</span>} />
        <ul className="m-0 list-none p-0">
          {items.map((e) => {
            const open = openId === e.id;
            return (
              <li key={e.id} className="border-t border-[#1a1f27] first:border-t-0">
                <button type="button" aria-expanded={open} onClick={() => setOpenId(open ? null : e.id)} className={cx('grid w-full cursor-pointer grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left sm:grid-cols-[150px_minmax(0,1fr)_minmax(0,1.3fr)_auto]', open ? 'bg-accent-bg' : 'hover:bg-raised')}>
                  <span className="font-mono text-xs text-sub">{fmtTime(e.createdAt, tz, true)}</span>
                  <span className="truncate text-sm text-ink sm:order-none">{e.actor?.name ?? <span className="text-muted">system</span>}</span>
                  <span className="col-span-2 flex min-w-0 flex-wrap items-center gap-2 sm:col-span-1">
                    <Pill tone={actionTone(e.action)}>{e.action}</Pill>
                    <span className="truncate font-mono text-xs text-muted">
                      {e.entityType}
                      {e.entityId ? ` · ${e.entityId}` : ''}
                    </span>
                  </span>
                  <span className="hidden text-xs text-accent sm:inline" aria-hidden="true">
                    {open ? '▾' : '▸'}
                  </span>
                </button>
                {open && <Diff e={e} />}
              </li>
            );
          })}
        </ul>
        {pages.isLoading && <Empty>Loading…</Empty>}
        {!pages.isLoading && !items.length && <Empty>No entries match these filters.</Empty>}
        {pages.hasNextPage && (
          <div className="flex justify-center border-t border-[#1a1f27] p-3">
            <Button onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage}>
              {pages.isFetchingNextPage ? 'Loading…' : 'Load older entries'}
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}
